/*
 * The three maps below are all indexed with a computed key — an order's `status`,
 * `bundleTypeName` or `foodTypeName`, each a plain `string` off the feed.
 * Declared with the app's open-key pattern: the `_LITERAL` const keeps a
 * literal-key read narrow, while the `Record<string, … | undefined>` arm makes a
 * computed-key read `… | undefined`. `satisfies` alone adds no index signature,
 * so the computed reads would still be errors.
 */
const STATUS_COLORS_LITERAL = {
  new: 'blue',
  accepted: 'blue',
  error: 'error',
  unaccepted: 'error',
  'lost/stolen': 'error',
  'late delivery': 'error',
  error_cancelled: 'error',
  '>25% damaged/spoiled/unsafe/incomplete': 'error',
  '<25% damaged/spoiled/unsafe/incomplete': 'error',
  reshipped: 'yellow',
  'never arrived': 'error',
  'incomplete delivery': 'negative',
  exported: 'warning',
  sent: 'yellow',
  shipped: 'yellow',
  processed: 'yellow',
  in_process: 'yellow',
  delivered: 'yy',
  cancelled: 'zz',
  'partially-fulfilled': 'error',
  unfulfilled: 'error',
};

export const statusColors: typeof STATUS_COLORS_LITERAL & Record<string, string | undefined> =
  STATUS_COLORS_LITERAL;

const FOOD_TYPE_MAP_LITERAL = {
  'KICKSTARTER OPTION A': 'PK',
  'PANTRY OPTION 1A': 'PK',
  'GROCERY 35A': 'GB',
  'GROCERY 35B': 'GB',
  'STANDARD GROCERY BUNDLE': 'GB',
  'PANTRY ONLY BUNDLE': 'PK',
  'MONTHLY GROCERIES A': 'GB',
  'MONTHLY GROCERIES B': 'GB',
  'MONTHLY#1 A': 'GB',
  'MONTHLY#1 B': 'GB',
  'ALLERGEN-FREE GROCERY BUNDLE': 'GB',
  'FRESH LIST PRODUCE BOX': 'PB',
  'BIGGERS MARKET PRODUCE BOX': 'PB',
  'PREPARED MEAL': 'PM',
  'PRODUCE BOX': 'PB',
  'PRODUCE AND HEALTHY SNACKING': 'PS',
  'PRODUCE AND PANTRY': 'PP',
  'AF PRODUCE AND HEALTHY SNACKING': 'PS',
  'AF PRODUCE AND PANTRY': 'PP',
  'Diapers & Wipes': 'DW',
  'PANTRY KIT': 'PK',
  'PANTRY BOX': 'PK',
  'GROCERY BOX': 'GB',
  'GROCERY BOX $35': 'GB',
  'GROCERY BOX $45': 'GB',
  'HRX STANDARD MINI': 'PB',
  'HRX EASY TO EAT MINI': 'PB',
  'HRX STANDARD FULL': 'PB',
  'HRX EASY TO EAT FULL': 'PB',
  'MEAL KIT': 'MK',
  'KICKSTARTER KIT': 'PK',
  'KICKSTARTER PANTRY KIT': 'PK',
  'GROCERY BUNDLE': 'GB',
  'SNACK PACK': 'PB',
  'GROCERIES BUNDLE & PREPARED MEALS': 'GB',
  'PANTRY BUNDLE A ($75)': 'PK',
  'PRODUCE BUNDLE ($60)': 'PK',
  'PROTEIN BUNDLE A ($70)': 'PK',
  'SNACK BUNDLE A ($45)': 'PK',
  'PANTRY BUNDLE B ($60)': 'PK',
  'SNACK BUNDLE B ($65)': 'PK',
  'PROTEIN BUNDLE B ($60)': 'PK',
  'SC 10 MEAL BUNDLE': 'PM',
  'NC BUNDLE 1': 'PM',
  'NC BUNDLE 2': 'PM',
  W1B1X10: 'PM',
  W1B2X10: 'PM',
  W1B3X10: 'PM',
  W1B1X15: 'PM',
  W1B2X15: 'PM',
  W1B3X15: 'PM',
  W2B1X10: 'PM',
  W2B2X10: 'PM',
  W2B3X10: 'PM',
  W2B1X15: 'PM',
  W2B2X15: 'PM',
  W2B3X15: 'PM',
  W3B1X10: 'PM',
  W3B2X10: 'PM',
  W3B3X10: 'PM',
  W3B1X15: 'PM',
  W3B2X15: 'PM',
  W3B3X15: 'PM',
  W4B1X10: 'PM',
  W4B2X10: 'PM',
  W4B3X10: 'PM',
  W4B1X15: 'PM',
  W4B2X15: 'PM',
  W4B3X15: 'PM',
  'KEBRIBAYAH GROCERY BOX': 'GB',
  'PRODUCE + PANTRY': 'PP',
  'SUPPLEMENT PACK': 'SP',
  'MAGNOLIA (VEG) - MED': 'GB',
  'SOUL FOOD - MED': 'GB',
  'SABOR - MED': 'GB',
  'MAGNOLIA (VEG) - LG': 'GB',
  'SOUL FOOD - LG': 'GB',
  'SABOR - LG': 'GB',
  'KITCHEN SUPPLIES': 'NA',
};

export const foodTypeMap: typeof FOOD_TYPE_MAP_LITERAL & Record<string, string | undefined> =
  FOOD_TYPE_MAP_LITERAL;

const FOOD_NAME_MAP_LITERAL = {
  'KICKSTARTER OPTION A': 'Pantry Kit',
  'PANTRY OPTION 1A': 'Pantry Kit',
  'GROCERY 35A': 'Grocery Box',
  'GROCERY 35B': 'Grocery Box',
  'STANDARD GROCERY BUNDLE': 'Grocery Box',
  'PANTRY ONLY BUNDLE': 'Pantry Kit',
  'MONTHLY GROCERIES A': 'Grocery Box',
  'MONTHLY GROCERIES B': 'Grocery Box',
  'MONTHLY#1 A': 'Grocery Box',
  'MONTHLY#1 B': 'Grocery Box',
  'ALLERGEN-FREE GROCERY BUNDLE': 'Grocery Box',
  'FRESH LIST PRODUCE BOX': 'Produce Box',
  'BIGGERS MARKET PRODUCE BOX': 'Produce Box',
  'PREPARED MEAL': 'Prepared Meal',
  'PRODUCE BOX': 'Produce Box',
  'PANTRY KIT': 'Pantry Kit',
  'PANTRY BOX': 'Pantry Kit',
  'GROCERY BOX': 'Grocery Box',
  'GROCERY BOX $35': 'Grocery Box',
  'GROCERY BOX $45': 'Grocery Box',
  'HRX STANDARD MINI': 'Produce Box',
  'HRX EASY TO EAT MINI': 'Produce Box',
  'HRX STANDARD FULL': 'Produce Box',
  'HRX EASY TO EAT FULL': 'Produce Box',
  'MEAL KIT': 'Meal Kit',
  'KICKSTARTER KIT': 'Pantry Kit',
  'KICKSTARTER PANTRY KIT': 'Pantry Kit',
  'GROCERY BUNDLE': 'Grocery Box',
  'SNACK PACK': 'Produce Box',
  'GROCERIES BUNDLE & PREPARED MEALS': 'Grocery Box',
  'PANTRY BUNDLE A ($75)': 'Pantry Kit',
  'PRODUCE BUNDLE ($60)': 'Pantry Kit',
  'PROTEIN BUNDLE A ($70)': 'Pantry Kit',
  'SNACK BUNDLE A ($45)': 'Pantry Kit',
  'PANTRY BUNDLE B ($60)': 'Pantry Kit',
  'SNACK BUNDLE B ($65)': 'Pantry Kit',
  'PROTEIN BUNDLE B ($60)': 'Pantry Kit',
  'SC 10 MEAL BUNDLE': 'Prepared Meal',
  'NC BUNDLE 1': 'Prepared Meal',
  'NC BUNDLE 2': 'Prepared Meal',
  W1B1X10: 'Prepared Meal',
  W1B2X10: 'Prepared Meal',
  W1B3X10: 'Prepared Meal',
  W1B1X15: 'Prepared Meal',
  W1B2X15: 'Prepared Meal',
  W1B3X15: 'Prepared Meal',
  W2B1X10: 'Prepared Meal',
  W2B2X10: 'Prepared Meal',
  W2B3X10: 'Prepared Meal',
  W2B1X15: 'Prepared Meal',
  W2B2X15: 'Prepared Meal',
  W2B3X15: 'Prepared Meal',
  W3B1X10: 'Prepared Meal',
  W3B2X10: 'Prepared Meal',
  W3B3X10: 'Prepared Meal',
  W3B1X15: 'Prepared Meal',
  W3B2X15: 'Prepared Meal',
  W3B3X15: 'Prepared Meal',
  W4B1X10: 'Prepared Meal',
  W4B2X10: 'Prepared Meal',
  W4B3X10: 'Prepared Meal',
  W4B1X15: 'Prepared Meal',
  W4B2X15: 'Prepared Meal',
  W4B3X15: 'Prepared Meal',
  'KEBRIBAYAH GROCERY BOX': 'Grocery Box',
  'PRODUCE + PANTRY': 'Produce & Pantry',
  'SUPPLEMENT PACK': 'Supplement Pack',
  'MAGNOLIA (VEG) - MED': 'Grocery Box',
  'SOUL FOOD - MED': 'Grocery Box',
  'SABOR - MED': 'Grocery Box',
  'MAGNOLIA (VEG) - LG': 'Grocery Box',
  'SOUL FOOD - LG': 'Grocery Box',
  'SABOR - LG': 'Grocery Box',
  'KITCHEN SUPPLIES': 'Not Applicable',
  'PRODUCE AND HEALTHY SNACKING': 'Produce & Snacks',
  'PRODUCE AND PANTRY': 'Produce & Pantry',
  'AF PRODUCE AND HEALTHY SNACKING': 'Produce & Snacks',
  'AF PRODUCE AND PANTRY': 'Produce & Pantry',
};

export const foodNameMap: typeof FOOD_NAME_MAP_LITERAL & Record<string, string | undefined> =
  FOOD_NAME_MAP_LITERAL;
